"use client";

import React, { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import type { CarouselApi } from "@/components/ui/carousel";
const testimonials = [{
  id: 1,
  author: "Mike Henderson",
  position: "Landscape Contractor",
  quote: "Switching to ClifRock was the best business decision I've made. We're finishing high-end outdoor kitchens in two days instead of two weeks."
}, {
  id: 2,
  author: "Sarah Jenkins",
  position: "Pool Builder",
  quote: "The panel system allows us to create massive water features without the heavy machinery. Our profit margins have never been higher."
}, {
  id: 3,
  author: "David Rossi",
  position: "Hardscape Specialist",
  quote: "My clients are blown away by the stone texture. It looks and feels like natural rock, but installs with a fraction of the labor cost."
}];
export default function InstallerSuccessStories() {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15
  });
  useEffect(() => {
    if (!api) return;
    api.on("select", () => {
      setFadeIn(false);
      setTimeout(() => {
        setCurrent(api.selectedScrollSnap());
        setFadeIn(true);
      }, 180);
    });
    setCurrent(api.selectedScrollSnap());
    setFadeIn(true);
  }, [api]);
  const scrollPrev = useCallback(() => {
    api?.scrollPrev();
  }, [api]);
  const scrollNext = useCallback(() => {
    api?.scrollNext();
  }, [api]);
  return <section data-section-id="231238" id="installer-success-stories" ref={ref} className="bg-background text-foreground py-20 px-4">
      <div className={`max-w-4xl mx-auto transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        {}
        <div className="text-center mb-14">
          <p className="font-default text-sm font-semibold uppercase tracking-widest mb-3" style={{
          color: "var(--primary)"
        }}>
            Success Stories
          </p>
          <h2 className="font-default text-4xl md:text-5xl font-bold tracking-tight" style={{
          color: "var(--foreground)"
        }}>
            Installer Success Stories
          </h2>
          <div className="w-16 h-1 mx-auto mt-5" style={{
          background: "var(--primary)"
        }} />
        </div>

        {}
        <Carousel opts={{
        loop: true
      }} setApi={setApi} className="w-full">
          <CarouselContent>
            {testimonials.map((t, i) => <CarouselItem data-index={i} key={t.id}>
                <div className={`flex flex-col items-center text-center px-4 md:px-16 py-12 rounded-lg shadow-lg transition-opacity duration-300 ${fadeIn ? "opacity-100" : "opacity-0"}`} style={{
              background: "var(--card)"
            }}>
                  {}
                  <span className="font-default text-6xl leading-none mb-4 select-none opacity-20" style={{
                color: "var(--primary)"
              }} aria-hidden="true">
                    &ldquo;
                  </span>

                  {}
                  <blockquote className="font-default text-2xl md:text-3xl font-normal italic leading-relaxed mb-8 max-w-2xl" style={{
                color: "var(--card-foreground)"
              }}>
                    {t.quote}
                  </blockquote>

                  {}
                  <div className="flex items-center gap-1 mb-6">
                    {[1, 2, 3, 4, 5].map((star, si) => <Star key={si} className="size-5 fill-current" style={{
                  color: "var(--primary)"
                }} />)}
                  </div>

                  {}
                  <div>
                    <p className="font-default text-xl font-bold tracking-tight" style={{
                  color: "var(--card-foreground)"
                }}>
                      {t.author}
                    </p>
                    <p className="font-default text-sm font-semibold tracking-wider uppercase mt-1" style={{
                  color: "var(--muted-foreground)"
                }}>
                      {t.position}
                    </p>
                  </div>
                  
                  {}
                  <div className="mt-8 px-4 py-2 rounded-lg bg-primary/10 border border-primary/20">
                    <span className="font-default text-sm font-bold text-primary">+200% Revenue Growth</span>
                  </div>
                </div>
              </CarouselItem>)}
          </CarouselContent>

          {}
          <div className="flex items-center justify-center gap-6 mt-8">
            <Button variant="ghost" size="icon" onClick={scrollPrev} className="rounded-lg border-2 transition-all duration-200 hover:bg-primary hover:text-white" style={{
            borderColor: "var(--primary)",
            color: "var(--primary)"
          }} aria-label="Previous">
              <ChevronLeft className="size-5" />
            </Button>

            {}
            <div className="flex items-center gap-2">
              {testimonials.map((_, i) => <Button key={i} variant="ghost" onClick={() => api?.scrollTo(i)} className="rounded-md transition-all duration-300 p-0" style={{
              width: current === i ? "32px" : "12px",
              height: "6px",
              background: current === i ? "var(--primary)" : "var(--muted-foreground)",
              opacity: current === i ? 1 : 0.3,
              border: "none"
            }} aria-label={`Go to slide ${i + 1}`} />)}
            </div>

            <Button variant="ghost" size="icon" onClick={scrollNext} className="rounded-lg border-2 transition-all duration-200 hover:bg-primary hover:text-white" style={{
            borderColor: "var(--primary)",
            color: "var(--primary)"
          }} aria-label="Next">
              <ChevronRight className="size-5" />
            </Button>
          </div>
        </Carousel>
      </div>
    </section>;
}